// src/components/Settings.js

import React, { useState, useEffect, useContext } from 'react';
import { useColorSettings } from '../Common/ColorSettingsContext';
import { useLanguageSettings } from '../Common/LanguageSettingsContext';
import { storage } from '../../firebase';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import './Settings.css';
import Spinner from '../Common/Spinner';

import LogoUploadGroup from './LogoUploadGroup';
import ColorPickerGroup from './ColorPickerGroup';
import ProgressBar from './ProgressBar';
import ErrorMessage from './ErrorMessage';
import ButtonGroup from './ButtonGroup';
import LanguageSelector from './LanguageSelector';
import { useTranslation } from 'react-i18next';
import { DataContext } from '../../DataContext'; // Import DataContext

const Settings = ({ showToast }) => {
  const { t } = useTranslation();
  const { appSettings } = useColorSettings();
  const { language, updateLanguage } = useLanguageSettings();

  // Import updateSettings from DataContext
  const { updateSettings } = useContext(DataContext);

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(appSettings.logoUrl || '');
  const [primaryColor, setPrimaryColor] = useState(appSettings.primaryColor || '#bb8300');
  const [tempLanguage, setTempLanguage] = useState(language || 'en');
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    setLogoPreviewUrl(appSettings.logoUrl || '');
    setPrimaryColor(appSettings.primaryColor || '#bb8300');
  }, [appSettings.logoUrl, appSettings.primaryColor]);

  useEffect(() => {
    return () => {
      if (logoPreviewUrl && typeof logoPreviewUrl === 'string') {
        URL.revokeObjectURL(logoPreviewUrl);
      }
    };
  }, [logoPreviewUrl]);

  const handleLanguageChange = (e) => {
    const selectedLanguage = e.target.value;
    setTempLanguage(selectedLanguage);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setUploadProgress(0);

    let uploadedLogoUrl = appSettings.logoUrl || '';

    try {
      if (logoFile) {
        const storageRef = ref(storage, `logos/${logoFile.name}-${Date.now()}`);
        const uploadTask = uploadBytesResumable(storageRef, logoFile);

        await new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(progress);
            },
            (error) => {
              console.error('Error uploading logo:', error);
              setError(t('Error uploading logo.'));
              setLoading(false);
              reject(error);
            },
            async () => {
              uploadedLogoUrl = await getDownloadURL(uploadTask.snapshot.ref);
              resolve();
            }
          );
        });
      }

      // Use updateSettings from context
      await updateSettings({
        logoUrl: uploadedLogoUrl,
        primaryColor: primaryColor,
      });

      if (tempLanguage !== language) {
        await updateLanguage(tempLanguage);
      }

      showToast(t('settingsUpdatedSuccess'), 'success');
      setLogoFile(null);
      setUploadProgress(0);
    } catch (error) {
      console.error('Error updating settings:', error);
      setError(t('Error updating settings.'));
      showToast(t('settingsUpdatedError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setLogoFile(null);
    setLogoPreviewUrl(appSettings.logoUrl || '');
    setPrimaryColor(appSettings.primaryColor || '#bb8300');
    setTempLanguage(language || 'en');
    setError('');
    showToast(t('Settings reset to default values.'), 'info');
  };

  return (
    <div className="settings-container">
      <h2>{t('applicationSettingsTitle')}</h2>
      <form onSubmit={handleSubmit} className="settings-form">
        <div className="settings-form-row">
          <ColorPickerGroup primaryColor={primaryColor} setPrimaryColor={setPrimaryColor} />
          <LanguageSelector language={tempLanguage} handleLanguageChange={handleLanguageChange} />
        </div>
        <div className="settings-form-row">
          <LogoUploadGroup
            setLogoFile={setLogoFile}
            logoPreviewUrl={logoPreviewUrl}
            setLogoPreviewUrl={setLogoPreviewUrl}
          />
        </div>

        {uploadProgress > 0 && uploadProgress < 100 && (
          <ProgressBar uploadProgress={uploadProgress} />
        )}

        {error && <ErrorMessage error={error} />}

        <ButtonGroup loading={loading} onReset={handleReset} />
      </form>
    </div>
  );
};

export default Settings;
